//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router'
import { useSelector } from 'react-redux'
import forIn from 'lodash/forIn'

import { useFetchSessionUserQuery } from 'api/sessionUser'
import {
  useFetchTicketForCloneQuery,
  useCreateTicketMutation,
} from 'api/tickets'

import { RouteTicketingSystems, RouteTicketingSystemsTickets, RouteTicketingSystemsTicket } from 'routes'
import { getInitialValues } from 'utils/ticketFields'
import * as ticketSelector from 'selectors/ticket'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'
import TicketForm from 'components/App/TicketEditPage/TicketForm/Index'
import TicketPageSidebar from 'components/App/TicketPage/TicketPageSidebar/Index'

const TicketClonePage = () => {
  // Polling for avoid session ending
  useFetchSessionUserQuery({}, { pollingInterval: 60000 * 10 })

  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const isUpdating = useSelector(ticketSelector.isUpdating)

  const ticketingSystemDisplayId = params.ticketingSystemDisplayId || null
  const ticketId = params.ticketId || null

  const { data, isFetching } = useFetchTicketForCloneQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    ticketId: ticketId,
  })
  const [createTicket, { isLoading: isCreatingTicket }] = useCreateTicketMutation()

  const ticket = data?.ticket || {}
  const ticketingSystemName = ticket.ticketingSystemName
  const parentTicket = ticket.parentTicket || null
  const withHierarchy = ticket.hierarchyEnabled

  const onTicketClone = (values) => {
    const paramValues = {}
    forIn(values, (value, name) => {
      if (value !== null) {
        if (value._isAMomentObject) {
          paramValues[name] = value.unix()
        } else if (withHierarchy && name === 'parent') {
          paramValues[name] = value.shortLabel || value.value
        } else {
          paramValues[name] = value
        }
      }
    })

    const params = {
      ticket: paramValues,
    }

    createTicket({
      ticketingSystemDisplayId: ticketingSystemDisplayId,
      ticketParams: params,
    }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        const newTicketId = fulfilled.data.ticket.externalId
        // switch to the just created ticket
        navigate(RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId: newTicketId }))
      }
    })
  }

  const onCancel = () => {
    navigate(RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId }))
  }

  const getBreadcrumbs = () => {
    const ticketingSystemsUrl = RouteTicketingSystems()
    const ticketingSystemUrl = RouteTicketingSystemsTickets({ ticketingSystemDisplayId })
    const ticketUrl = RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId })

    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketingSystems'),
      url: ticketingSystemsUrl,
    }, {
      text: ticketingSystemName,
      url: ticketingSystemUrl,
    }, {
      text: ticket.externalId,
      url: ticketUrl,
    }, {
      text: t('common.breadcrumbTicketClone'),
    }]
    return breadcrumbsList
  }

  // canBeCloned will be undefined if ticketing system was not found so we may avoid
  // checking the 'Ticket not found' message. It will show 404 page in this case anyway
  if (data && !ticket.canBeCloned) {
    return <ErrorPage404 />
  }

  const title = t('ticketClonePage.pageHeader', { 'tsName': ticketingSystemName, interpolation: { escapeValue: false } })
  const isLoading = isFetching || isCreatingTicket || isUpdating
  const editableFields = ticket.bodyFields || []
  const initialValues = getInitialValues(editableFields, parentTicket)

  return (
    <MainWrap pageTitle={title}>
      <TicketPageSidebar ticket={ticket} isClone />
      <Content loading={isLoading}>
        <Header
          title={title}
          breadcrumbsList={getBreadcrumbs()}
        />
        <TicketForm
          isClone
          fields={editableFields}
          initialValues={initialValues}
          ticketId={ticketId}
          ticketingSystemDisplayId={ticketingSystemDisplayId}
          onSubmit={onTicketClone}
          onCancel={onCancel}
          withHierarchy={withHierarchy}
          parentTicket={parentTicket}
        />
      </Content>
    </MainWrap>
  )
}

export default TicketClonePage
